/**
 * The channel a notification's patient copy travels on.
 *
 * Shared rather than owned by one tool: the Scheduler and Payments both let a
 * therapist choose it, per notification, with the same three values.
 *
 * WhatsApp and Both are a permission to try, never a decision to send: only a
 * patient who accepted WhatsApp in their own portal is written there, and
 * everyone else gets the email.
 */
export type NoticeChannel = "email" | "whatsapp" | "email_and_whatsapp";

/** Display order of the control. */
export const NOTICE_CHANNELS: readonly NoticeChannel[] = [
    "email",
    "whatsapp",
    "email_and_whatsapp",
];

/** Whether a channel lets WhatsApp be tried (WhatsApp and Both). */
export const allowsWhatsApp = (channel: NoticeChannel): boolean => channel !== "email";

/**
 * What the patient copy actually travels on, for describing it. A row the
 * deployment cannot send on WhatsApp behaves as email; the stored value is
 * never rewritten (it takes effect once the template is approved).
 */
export function effectiveNoticeChannel(
    stored: NoticeChannel,
    whatsappAvailable: boolean
): NoticeChannel {
    return whatsappAvailable ? stored : "email";
}

export type ChannelChoice = "hidden" | "fixedEmail" | "choose";

/**
 * hidden: row off, or patient not a recipient. fixedEmail: no WhatsApp for
 * this row. choose: offer the radio group.
 *
 * The stored channel is only read on `choose`, which needs WhatsApp to be
 * available — and only a backend that reports availability sends the channel.
 */
export function channelChoice(input: {
    enabled: boolean;
    toPatient: boolean;
    whatsappAvailable: boolean;
}): ChannelChoice {
    if (!input.enabled || !input.toPatient) return "hidden";
    return input.whatsappAvailable ? "choose" : "fixedEmail";
}
